import { useState, useEffect, useCallback, useRef } from 'react';
import { getAudienceSocket } from '../hooks/useAudienceState';
import NicknameBar from '../components/NicknameBar';
import { useCountdown } from '../hooks/useCountdown';

interface PixelArtChallengeScreenProps {
  challengeId: string;
  config: {
    durationSeconds: number;
    pixelartNumColors?: number;
  };
  endsAt: number;
  serverTime: number;
  numColors: number;
  initialUserGrid?: number[][] | null;
}

const DEFAULT_PALETTE = [
  '#6b7280', // 0 = vuoto/sfondo (grigio)
  '#ef4444', // 1 = rosso
  '#3b82f6', // 2 = blu
  '#22c55e', // 3 = verde
  '#eab308', // 4 = giallo
  '#a855f7', // 5 = viola
  '#f97316', // 6 = arancione
  '#ec4899', // 7 = rosa
  '#06b6d4', // 8 = ciano
];

function PixelArtChallengeScreen({
  challengeId,
  config,
  endsAt,
  serverTime,
  numColors,
  initialUserGrid,
}: PixelArtChallengeScreenProps) {
  const effectiveNumColors = numColors || config.pixelartNumColors || 4;
  const remainingSeconds = useCountdown(endsAt, serverTime);
  const [personalAccuracy, setPersonalAccuracy] = useState(0);
  const [selectedColor, setSelectedColor] = useState(1);
  const [grid, setGrid] = useState<number[][]>(() => {
    if (initialUserGrid) return initialUserGrid;
    const g: number[][] = [];
    for (let r = 0; r < 10; r++) {
      g[r] = new Array(10).fill(0);
    }
    return g;
  });

  // Ascolta aggiornamenti precisione personale dal server
  useEffect(() => {
    const socket = getAudienceSocket();
    if (!socket) return;

    const handlePersonalAccuracy = (data: any) => {
      if (data.challengeId === challengeId) {
        setPersonalAccuracy(data.personalAccuracy);
      }
    };

    socket.on('challenge:personal-accuracy', handlePersonalAccuracy);
    return () => { socket.off('challenge:personal-accuracy', handlePersonalAccuracy); };
  }, [challengeId]);

  // Flag per evitare doppio fire touch+mouse
  const touchedRef = useRef(false);

  // Gestisci tap su cella
  const handleCellTap = useCallback((row: number, col: number) => {
    const socket = getAudienceSocket();
    if (!socket) return;

    const currentColor = grid[row][col];
    // Se la cella ha gia' il colore selezionato, cancella (torna a 0)
    const newColor = currentColor === selectedColor ? 0 : selectedColor;

    // Se il colore non cambia, non fare nulla
    if (newColor === currentColor) return;

    setGrid(prev => {
      const newGrid = prev.map(r => [...r]);
      newGrid[row][col] = newColor;
      return newGrid;
    });

    socket.emit('challenge:tap', {
      challengeId,
      variant: 'pixelart',
      row,
      col,
      color: String(newColor),
    });
  }, [challengeId, grid, selectedColor]);

  // Palette disponibile (solo colori 1..numColors)
  const palette = Array.from({ length: effectiveNumColors }, (_, i) => i + 1);

  return (
    <div
      className="relative flex flex-col min-h-screen overflow-hidden select-none parchment-bg"
      style={{ touchAction: 'manipulation' }}
    >
      <NicknameBar />

      {/* Header: countdown + accuracy */}
      <div className="pt-12 px-4 pb-2 z-10">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-2xl font-heading font-bold ${remainingSeconds <= 5 ? 'text-team-red animate-pulse' : 'text-ink'}`}>
            {remainingSeconds}s
          </span>
          <span className="text-ink-light text-base font-heading">
            La tua precisione: {Math.round(personalAccuracy)}%
          </span>
        </div>
        {/* Progress bar */}
        <div className="progress-parchment">
          <div
            className="h-full bg-gradient-to-r from-orange-500 to-amber-400 rounded-full transition-all duration-300"
            style={{ width: `${Math.min(100, personalAccuracy)}%` }}
          />
        </div>
      </div>

      {/* Color picker */}
      <div className="px-4 py-3 flex justify-center gap-2 flex-wrap">
        {palette.map(colorIdx => (
          <button
            key={colorIdx}
            onClick={() => setSelectedColor(colorIdx)}
            className="rounded-full transition-all"
            style={{
              width: 36,
              height: 36,
              backgroundColor: DEFAULT_PALETTE[colorIdx] || '#888',
              border: selectedColor === colorIdx ? '3px solid white' : '3px solid transparent',
              boxShadow: selectedColor === colorIdx ? '0 0 8px rgba(255,255,255,0.5)' : 'none',
              transform: selectedColor === colorIdx ? 'scale(1.15)' : 'scale(1)',
            }}
          />
        ))}
        {/* Gomma */}
        <button
          onClick={() => setSelectedColor(0)}
          className="rounded-full transition-all flex items-center justify-center text-lg"
          style={{
            width: 36,
            height: 36,
            backgroundColor: '#374151',
            border: selectedColor === 0 ? '3px solid white' : '3px solid transparent',
            boxShadow: selectedColor === 0 ? '0 0 8px rgba(255,255,255,0.5)' : 'none',
            transform: selectedColor === 0 ? 'scale(1.15)' : 'scale(1)',
          }}
        >
          &#10060;
        </button>
      </div>

      {/* Griglia 10x10 */}
      <div className="flex-1 flex items-center justify-center px-2 pb-4">
        <div
          className="grid gap-[1px] bg-gray-700 p-[1px] rounded-lg"
          style={{
            gridTemplateColumns: 'repeat(10, 1fr)',
            width: 'min(90vw, 360px)',
            height: 'min(90vw, 360px)',
          }}
        >
          {grid.map((row, r) =>
            row.map((cell, c) => (
              <button
                key={`${r}-${c}`}
                className="aspect-square transition-colors duration-100"
                style={{
                  backgroundColor: cell === 0 ? '#1f2937' : (DEFAULT_PALETTE[cell] || '#888'),
                  minWidth: 0,
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  touchedRef.current = true;
                  handleCellTap(r, c);
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (touchedRef.current) {
                    touchedRef.current = false;
                    return;
                  }
                  handleCellTap(r, c);
                }}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default PixelArtChallengeScreen;
